import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

// Canonical page identifiers that match the dashboard_page enum.
export const PAGES = [
  'overview',
  'transactions',
  'wallets',
  'treasury',
  'compliance',
  'agent_reports',
  'careers',
  'support',
  'documents',
  'pitch_access',
  'settings',
];

export const PAGE_LABEL = {
  overview:      'Overview',
  transactions:  'Transactions',
  wallets:       'Wallets',
  treasury:      'Treasury',
  compliance:    'Compliance',
  agent_reports: 'Agent Reports',
  careers:       'Careers',
  support:       'Support',
  documents:     'Documents',
  pitch_access:  'Pitch Access',
  settings:      'Settings',
};

const ROLE_DEFAULTS = {
  super_admin:        new Set(PAGES),
  admin:              new Set(PAGES),
  support_engineer:   new Set(['overview', 'support']),
  // Compliance officer needs Treasury to approve/deny Category C reversals (A16/A17).
  compliance_officer: new Set(['overview', 'compliance', 'agent_reports', 'treasury']),
  // Note: 'careers' is intentionally NOT in any role default below super_admin.
  // It's granted per-user via Settings → Users → grant pages.
  member:             new Set(['overview']),
};

export const ROLE_LABEL = {
  super_admin:        'Super admin',
  admin:              'Super admin',
  support_engineer:   'Support engineer',
  compliance_officer: 'Compliance officer',
  member:             'Member',
};

const isSuperAdminRole = (r) => r === 'super_admin' || r === 'admin';

export const usePagePermissions = () => {
  const { profile } = useAuth();
  const [explicitPages, setExplicitPages] = useState(new Set());
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!isSupabaseConfigured) {
      // Mock-mode: treat the demo user as super admin.
      setExplicitPages(new Set());
      setLoaded(true);
      return;
    }
    if (!supabase || !profile?.id) {
      setExplicitPages(new Set());
      setLoaded(true);
      return;
    }
    const { data, error } = await supabase
      .from('user_page_permissions')
      .select('page')
      .eq('user_id', profile.id);
    if (error) {
      // eslint-disable-next-line no-console
      console.error('[perms] failed to load grants', error);
      // If the token is stale (PGRST301 = JWT expired) we fire signOut so
      // AuthContext's onAuthStateChange clears the session, ProtectedRoute
      // redirects to /signin, and the user isn't left half-loaded with
      // empty permissions.
      const msg = (error.message || '').toLowerCase();
      const isAuthError =
        error.code === 'PGRST301' ||
        error.status === 401 ||
        msg.includes('jwt expired') ||
        msg.includes('invalid jwt');
      if (isAuthError) {
        try { await supabase.auth.signOut(); } catch (_) { /* best-effort */ }
        return; // don't flip `loaded=true`; session is about to drop and
                // ProtectedRoute will take over.
      }
      setExplicitPages(new Set());
    } else {
      setExplicitPages(new Set((data || []).map((r) => r.page)));
    }
    setLoaded(true);
  }, [profile?.id]);

  useEffect(() => { refresh(); }, [refresh]);

  const role = profile?.role || 'member';
  const isSuperAdmin = isSuperAdminRole(role) && profile?.status === 'approved';

  const rolePages = ROLE_DEFAULTS[role] || ROLE_DEFAULTS.member;
  const allPages = new Set([...rolePages, ...explicitPages, 'overview']);

  const hasPage = (page) => isSuperAdmin || allPages.has(page);

  return {
    loaded,
    role,
    isSuperAdmin,
    pages: allPages,
    rolePages,
    explicitPages,
    hasPage,
    refresh,
  };
};
